import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  ItemResponse,
  ItemService,
} from '@nebalkon-workspace/storage-api';
import { parseCatalogItemIdsParam } from '../utils/catalog-item-ids.util';

@Component({
  selector: 'lib-item-selection-chips',
  imports: [TranslatePipe],
  templateUrl: './item-selection-chips.component.html',
  styleUrl: './item-selection-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemSelectionChipsComponent {
  readonly #itemService = inject(ItemService);
  readonly #destroyRef = inject(DestroyRef);

  /** Comma-separated UUIDs or array of ids. */
  readonly itemIds = input<string[] | string>([]);
  readonly removable = input(true);

  readonly itemIdsChange = output<string[]>();

  readonly loading = signal(false);
  readonly items = signal<ItemResponse[]>([]);

  constructor() {
    effect(() => {
      const ids = this.#resolveIds();
      this.#loadItems(ids);
    });
  }

  removeItem(id: string): void {
    const next = this.#resolveIds().filter((itemId) => itemId !== id);
    this.itemIdsChange.emit(next);
  }

  #resolveIds(): string[] {
    const raw = this.itemIds();
    if (Array.isArray(raw)) {
      return raw;
    }
    return parseCatalogItemIdsParam(raw);
  }

  #loadItems(ids: string[]): void {
    if (ids.length === 0) {
      this.items.set([]);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    forkJoin(
      ids.map((id) =>
        this.#itemService.getItem(id).pipe(catchError(() => of(null))),
      ),
    )
      .pipe(
        map((responses) =>
          responses.filter((item): item is ItemResponse => item !== null),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: (loaded) => {
          this.items.set(loaded);
          this.loading.set(false);
        },
        error: () => {
          this.items.set([]);
          this.loading.set(false);
        },
      });
  }
}
